import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@core/services';
import { AuthService } from '@customer/services';
import { Register as RegisterService } from './register.svc';
import { ConfirmPasswordValidator } from '@core/validators';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '@core/components';

interface SecureAuthUser {
  UserName?: string;
}

interface PortalInvitationResponse {
  Email?: string;
  InviteStatus?: number;
  IsEligible?: boolean;
  UserName?: string;
  AccountId?: string;
  ContactId?: string;
}

@Component({
  selector: 'oke-register',
  providers: [RegisterService],
  imports: [ToastComponent, ReactiveFormsModule],
  templateUrl: './register.html',
  styleUrl: './register.scss'
})
export class Register implements OnInit, OnDestroy {
  private readonly _fb = inject(FormBuilder);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _authSvc = inject(AuthService);
  private readonly _toastSvc = inject(ToastService);
  private readonly _registerSvc = inject(RegisterService);
  secureAuthUser: SecureAuthUser | null = null;
  registerForm!: FormGroup;
  emailForm!: FormGroup;
  private readonly isFormSubmission = false;
  inviterAccountId = '';
  inviterContactId = '';
  bodyElem = document.body;
  headElem = document.getElementById('app-header');
  isLoadingSpinner = false;
  validUserEmail = '';
  emailRegEx =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
  pinRegEx = /^(?=[A-Za-z0-9@#$%^&+!=.]+$)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+!=.])(?=.{8,}).*$/;
  isSuccessfulFormSubmission = false;
  mobileRegex = /^(\+\d{1,3}[- ]?)?\d{10}$/;
  isCusUser = false;
  isAdUser = false;
  validInvitationStatus = [0, 1];

  ngOnInit(): void {
    this.initializeExternalView();
    this._toastSvc.dismissAll();
    this.emailForm = this._fb.group({
      email: ['', [Validators.required, Validators.pattern(this.emailRegEx)]]
    });
  }

  submitEmail(): void {
    if (this.emailForm.invalid) {
      return;
    }

    this._toastSvc.dismissAll();
    this.isLoadingSpinner = true;
    const portalInvitationCode = this._route.snapshot.params['invitationId'];
    const email = (this.emailForm.controls.email.value as string).trim();

    this._registerSvc.validatePortalInvitation(portalInvitationCode, email).subscribe({
      next: (invitation: PortalInvitationResponse) => {
        this.validUserEmail = invitation?.Email ?? '';
        const invitationStatus = invitation?.InviteStatus;

        if (
          invitation?.IsEligible &&
          this.validInvitationStatus.includes(invitationStatus ?? -1) &&
          this.emailRegEx.test(this.validUserEmail)
        ) {
          this.inviterAccountId = invitation.AccountId ?? '';
          this.inviterContactId = invitation.ContactId ?? '';
          this.secureAuthUser = { UserName: invitation!.UserName } satisfies SecureAuthUser;
          this.isCusUser = invitation!.UserName ? this._authSvc.isCusUser(invitation!.UserName) : false;
          this.isAdUser = !this.isCusUser && !!invitation!.UserName;
          this.isLoadingSpinner = false;
          if (!this.isCusUser && !this.isAdUser) {
            this.initializeForm();
          } else {
            const payload = {
              Id: this.inviterContactId,
              AccountId: this.inviterAccountId,
              UserName: invitation.UserName,
              Email: invitation.Email,
              InviteCode: this._route.snapshot.params['invitationId'],
              IsCusUser: this.isCusUser
            };
            this.registerCustomerPortal(payload);
          }
        } else {
          this._handleUserLookupError(undefined);
        }
      },
      error: (error: HttpErrorResponse) => this._handleUserLookupError(error)
    });
  }

  private _handleUserLookupError(error?: HttpErrorResponse): void {
    this.isLoadingSpinner = false;

    this.showErrorMessage('Issues in Verifying the Invitation.');
  }

  ngOnDestroy(): void {
    document.getElementsByClassName('grecaptcha-badge')[0]?.remove();
  }

  initializeForm(): void {
    if (!this.isCusUser && !this.isAdUser) {
      this.registerForm = this._fb.group(
        {
          firstName: ['', [Validators.required, Validators.maxLength(15)]],
          lastName: ['', [Validators.required, Validators.maxLength(15)]],
          companyName: ['', [Validators.required, Validators.maxLength(50)]],
          email: [this.validUserEmail, [Validators.required, Validators.pattern(this.emailRegEx)]],
          mobile: ['', [Validators.pattern(this.mobileRegex)]],
          pin: ['', [Validators.required, Validators.minLength(8), Validators.pattern(this.pinRegEx)]],
          confirmPin: ['', Validators.required]
        },
        {
          validator: ConfirmPasswordValidator('pin', 'confirmPin')
        }
      );
    }
  }

  registerSecureAuth(): void {
    if (
      this.registerForm.valid &&
      this.validUserEmail.toLocaleLowerCase() === (this.registerForm.controls.email.value as string).toLocaleLowerCase()
    ) {
      this.isLoadingSpinner = true;
      this._toastSvc.dismissAll();
      const currentTimeStamp = new Date().getTime().toString().slice(-5);
      const payload = {
        UserName: this.secureAuthUser?.UserName
          ? this.secureAuthUser.UserName
          : `${this.registerForm.controls.firstName.value}${currentTimeStamp}`,
        FirstName: this.registerForm.controls.firstName.value as string,
        LastName: this.registerForm.controls.lastName.value as string,
        Email: this.registerForm.controls.email.value as string,
        CompanyName: this.registerForm.controls.companyName.value as string,
        Phone: (this.registerForm.controls.mobile.value as string) ?? '',
        Id: this.inviterContactId,
        InviteCode: this._route.snapshot.params['invitationId'],
        Pin: this._authSvc.eTest(this.registerForm.controls.pin.value),
        IsCusUser: this.isCusUser,
        AccountId: this.inviterAccountId
      };

      this.registerCustomerPortal(payload);
    }
  }

  registerCustomerPortal(registerFormData: any): void {
    this.isLoadingSpinner = true;
    this._toastSvc.dismissAll();

    this._registerSvc.inviteCodePortalInvitation(registerFormData).subscribe({
      next: () => {
        this.isLoadingSpinner = false;
        if (!this.isCusUser && !this.isAdUser) {
          this._router.navigate(['/login']);
        } else {
          this.isSuccessfulFormSubmission = true;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoadingSpinner = false;
        this._handleUserLookupError(error);
      }
    });
  }

  protected goToLogin(): void {
    this._router.navigate(['/login']);
  }

  isFieldInvalid(field: string): boolean {
    return (
      (!this.registerForm.get(field)?.valid && this.registerForm.get(field)?.touched) ||
      (this.registerForm.untouched && this.isFormSubmission)
    );
  }

  //Remove internal styling from body of app (Note: Should be modified to using NG Class for conditional styling)
  initializeExternalView(): void {
    this.bodyElem.classList.add('is-unauthenticated-register');
    this.bodyElem.classList.remove('is-unauthenticated');
  }

  //Apply internal styling to body of app (Note: Should be modified to using NG Class for conditional styling)
  initializeInternalView(): void {
    this.bodyElem.classList.remove('is-unauthenticated-register');
    this.bodyElem.classList.remove('is-unauthenticated');
  }

  restoreLoginPageView(): void {
    this.bodyElem.classList.remove('is-unauthenticated-register');
    this.bodyElem.classList.add('is-unauthenticated');
  }

  showErrorMessage(message: string): void {
    this._toastSvc.dismissAll();
    this._toastSvc.alert('Registration Problem', message, {
      shouldShowHelpCenter: false,
      shouldShowServiceDesk: true
    });
  }
}
